import React, { useState, useEffect, memo } from "react";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import styled from "styled-components";
import { PrimaryCTAButton, PrimaryOutlinedCloseCTAButton } from "../Buttons";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import BundleService from "../../../services/BundleService";
import { Modal, Checkbox } from "@material-ui/core";
import { getErrorMessage } from "../../../helpers/functions";
import { CustomInputField } from "../FormInputs";
import {
  ModalHeader,
  HeaderLeft,
  CloseBtn,
  SubTitle,
  BundleName,
  Divider,
  ScrollArea,
  DocCard,
  DocCardTop,
  DocCheckRow,
  DocName,
  SelectedBadge,
  FieldsRow,
  FieldGroup,
  FieldLabel,
  EmptyState,
  EmptyText,
  FooterContainer,
  SelectionInfo,
  DotIndicator,
  ButtonGroup,
} from "./index";

const AddPages = ({ id, add, setAdd, setRefreshDetails, docName, title }) => {
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  const [selectedArray, setSelectedArray] = useState(null);

  useEffect(() => {
    let initialArray = [];
    if (docName) {
      docName.map((item) => {
        let newObj = { ...item };
        newObj.checked = false;
        initialArray.push(newObj);
        return initialArray;
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docName]);

  const updateBundleDoc = async () => {
    try {
      setLoader({ state: true, message: "Add page..." });
      const payload = {
        bundleName: title,
        bundleDocuments: [{ ...selectedArray }],
      };
      const res = await BundleService.addPagesToBundle(id, payload);
      if (res) {
        enqueueSnackbar(res?.message, {
          variant: "success",
        });
        setRefreshDetails(true);
      }
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, {
        variant: "error",
      });
    } finally {
      setLoader({ state: false });
      setAdd(false);
      setSelectedArray(null);
    }
  };

  const handleSelect = (item, event) => {
    if (event.target.checked) {
      let newObj = { ...item };
      newObj.checked = true;
      setSelectedArray(newObj);
    }
  };

  return (
    <Modal
      open={add}
      onClose={() => setAdd(false) || setSelectedArray(null)}
      disableRestoreFocus={true}
    >
      <DrawerContainer role="presentation">
        <Container>
          {/* Header */}
          <ModalHeader>
            <HeaderLeft>
              <Heading>Add Page</Heading>
            </HeaderLeft>
            <CloseBtn onClick={() => setAdd(false)} aria-label="Close">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </CloseBtn>
          </ModalHeader>

          {/* Subtitle */}
          {title && (
            <SubTitle>
              Bundle: <BundleName>{title}</BundleName>
            </SubTitle>
          )}

          <Divider />

          {/* Doc list */}
          <ScrollArea hasMany={docName?.length > 3}>
            {docName && docName.length > 0 ? (
              docName.map((item, index) => {
                const isSelected =
                  selectedArray && selectedArray.id === item.id;
                return (
                  <DocCard key={item.name + index} selected={isSelected}>
                    <DocCardTop>
                      <DocCheckRow>
                        <CustomCheckbox
                          onChange={(ev) => handleSelect(item, ev)}
                          checked={isSelected || false}
                          name={item?.name + index}
                        />
                        <DocName selected={isSelected}>{item.name}</DocName>
                      </DocCheckRow>
                      {isSelected && <SelectedBadge>Selected</SelectedBadge>}
                    </DocCardTop>

                    <FieldsRow>
                      <FieldGroup className="bundle-doc">
                        <FieldLabel>Page No.</FieldLabel>
                        <CustomInputField
                          className="input-white"
                          variant="outlined"
                          value={
                            isSelected ? selectedArray.pageNo : item?.pageNo
                          }
                          disabled={!isSelected}
                          onChange={(event) =>
                            setSelectedArray({
                              ...selectedArray,
                              pageNo: event.target.value,
                            })
                          }
                        />
                      </FieldGroup>
                      <FieldGroup className="bundle-doc">
                        <FieldLabel>Position</FieldLabel>
                        <CustomInputField
                          className="input-white"
                          variant="outlined"
                          type="number"
                          disabled={!isSelected}
                          value={isSelected ? selectedArray.position : ""}
                          onChange={(event) =>
                            setSelectedArray({
                              ...selectedArray,
                              position: event.target.value,
                            })
                          }
                        />
                      </FieldGroup>
                    </FieldsRow>
                  </DocCard>
                );
              })
            ) : (
              <EmptyState>
                <svg
                  width="36"
                  height="36"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#acb1c2"
                  strokeWidth="1.5"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <EmptyText>No documents available</EmptyText>
              </EmptyState>
            )}
          </ScrollArea>

          <Divider />

          {/* Footer */}
          <FooterContainer>
            {selectedArray && (
              <SelectionInfo>
                <DotIndicator />1 document selected
              </SelectionInfo>
            )}
            <ButtonGroup>
              <PrimaryOutlinedCloseCTAButton
                style={{ flex: 1, maxWidth: 160 }}
                onClick={() => setAdd(false)}
              >
                Cancel
              </PrimaryOutlinedCloseCTAButton>
              <PrimaryCTAButton
                style={{ flex: 1, maxWidth: 160 }}
                disabled={selectedArray === null}
                onClick={updateBundleDoc}
              >
                Submit
              </PrimaryCTAButton>
            </ButtonGroup>
          </FooterContainer>
        </Container>
      </DrawerContainer>
    </Modal>
  );
};

export default memo(AddPages);

const CustomCheckbox = ({ ...props }) => (
  <Checkbox
    color={"primary"}
    icon={
      <img src={require("../../../assets/images/checkbox.svg")} alt="check" />
    }
    checkedIcon={
      <img
        src={require("../../../assets/images/checkBoxSelected.svg")}
        alt="check"
      />
    }
    {...props}
  />
);

const Container = styled.div`
  margin: 0 15px;
  background-color: white;
  outline: none;
  border-radius: 8px;
  @media ${theme?.breakpoints?.sm_up} {
    margin: 0;
  }
`;

const DrawerContainer = styled.div`
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  @media ${theme?.breakpoints?.sm_up} {
    max-width: 50%;
  }
`;

const Heading = styled.h3`
  margin: 0;
  padding: 0;
  font-size: 18px;
  font-weight: 800;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: ${COLORS.COLOR_DARK};
  font-family: ${theme.fonts.primaryFontExtraBold};
`;

export const Label = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 10px;
  text-transform: capitalize;
  font-weight: 600;
  line-height: 1.7;
  margin-bottom: 6px;
  color: ${COLORS.COLOR_DARK};
  text-align: left;
  & span {
    padding: 0px;
  }
`;
